import React, { useEffect } from 'react'

import {
  Card,
  Form,
  Grid,
  Input,
  InputMask,
  SelectForm,
  SimpleCollapse,
  Button,
} from 'components'
import { useTabs, useLoading, useModal, useException } from 'hooks'
import { useForm } from 'react-hook-form'
import { IResponseTypes } from 'services/core/genericEntityGST'
import { GradacaoServices, IGradacaoByIdResponse, IGraduacaoMultasGetAllResponse } from 'services/GST/GraduacaoMultas'
import { SPACING } from 'utils'

import { useFineGraduation } from '../../context'
import { IForm } from './types'

const FineGraduation: React.FC = () => {
  const { showModal } = useModal()
  const { setLoading } = useLoading()
  const { getException, createCustomException } = useException()
  
  const {
    control,
    handleSubmit,
    reset: resetFields,
    setValue,
    formState: { errors }
  } = useForm<IForm>()
  
  const {
    multaSelecionadaParaEdicao,
    handleClearMultaSelecionada,
    handleGetAreas,
    handleGetInfracoes,
    handleGetQuantidadeFuncionarios,
    areasList,
    faixaFuncionariosList,
    infracoesList,
  } = useFineGraduation()

  const handleSubmitForm = (form: IForm) => {
    setLoading('loading')

    const actionName = multaSelecionadaParaEdicao ? 'update' : 'create'

    const actions = {
      create: GradacaoServices().createGraduacaoMulta,
      update: GradacaoServices().updateGraduacaoMulta
    }

    actions[actionName]({
      faixaId: form.faixaId,
      flTipoMulta: form.flTipoMulta,
      idGradacao: actionName === 'update' ? multaSelecionadaParaEdicao.idGradacaoMulta : 0,
      infracaoId: form.infracaoId,
      qtUfir: form.qtUfir
    })
      .then(response => {
        if (response.status !== 200) {
          const { detalhe, mensagem, tipo } = response.data
          createCustomException({
            detalhe,
            message: detalhe || mensagem || 'Ocorreu um erro ao carregar os filtros.',
            tipo: IResponseTypes[tipo]
          })
          throw new Error(response.data.mensagem || 'Não foi possível criar o registro da Gradação de Multa')
        }

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: response?.data?.mensagem || 'Dados salvos com sucesso.',
          txtButtonConfirm: 'OK',
          type: 'success'
        })
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error'
        })
      })
  }
  
  const handleGetGradacaoById = (gradacaoId: number) => {
    setLoading('loading')

    GradacaoServices()
      .getGradacaoById({
        idGradacao: gradacaoId
      })
      .then(response => {
        if (response.status !== 200) {
          const { detalhe, mensagem, tipo } = response.data
          createCustomException({
            detalhe,
            message: detalhe || mensagem || 'Ocorreu um erro ao carregar os filtros.',
            tipo: IResponseTypes[tipo]
          })
          throw new Error(response.data.mensagem || 'Não foi possível criar o registro da Gradação de Multa')
        }

        fillValuesForEditing(response?.data?.entity)

        setLoading('idle')
      })
      .catch((e: Error) => {
        const error = getException(e)

        setLoading('idle')
        showModal({
          hideButtonCancel: true,
          message: error.message,
          txtButtonConfirm: 'OK',
          type: error.tipo || 'error'
        })
      })
  }

  

  const clearGradacaoMultas = () => {
    if (multaSelecionadaParaEdicao) {
      handleClearMultaSelecionada()
    }

    resetFields()
  }

  const fillValuesForEditing = (gradacaoByIdObj?: IGradacaoByIdResponse) => {
    // Pode receber tanto o valor do getById como os vindos da tela de pesquisa
    const objToSetFormValues: IGradacaoByIdResponse | IGraduacaoMultasGetAllResponse = 
      gradacaoByIdObj ? gradacaoByIdObj : multaSelecionadaParaEdicao

    Object.keys(objToSetFormValues).forEach((field: keyof IForm) => {
      const value = objToSetFormValues[field]

      setValue(field, value)
    })
  }

  useTabs(null, {
    onExitTab: clearGradacaoMultas
  })

  useEffect(() => {
    if (!areasList && !faixaFuncionariosList && !infracoesList) {
      handleGetAreas()
      handleGetInfracoes()
      handleGetQuantidadeFuncionarios()
    }
  }, [areasList, faixaFuncionariosList, infracoesList])

  useEffect(() => {
    if (multaSelecionadaParaEdicao) {
      handleGetGradacaoById(multaSelecionadaParaEdicao?.idGradacaoMulta)
    }
  }, [])

  return (
    <>
      <Card
        marginBottom={SPACING.spacing100}
        padding={SPACING.spacing20}
        boxShadow={`0px -1px 32px rgba(50, 50, 71, 0.12), 0px 24px 32px rgba(50, 50, 71, 0.12)`}
        borderRadius={`0px 10px 0px 0px`}
      >
        <SimpleCollapse isExpanded title={`VALOR UFIR: ${multaSelecionadaParaEdicao?.qtdUFIR || '----'}`}>
          <Form
            onSubmit={handleSubmit(handleSubmitForm)}
          >
            <Grid
              gridCols="2fr 2fr 2fr 2fr 2fr 2fr"
              gridGap={`0 ${SPACING.spacing20}`}
              margin={`${SPACING.spacing10} 0 ${SPACING.spacing10} 0`}
            >
              <SelectForm
                control={control}
                errors={errors}
                defaultValue={''}
                placeholder="Selecione"
                listOptions={areasList?.map((areaAtuacao) => (
                  { label: areaAtuacao?.descricao, value: areaAtuacao?.codigoDeAreaDeAtuacao}
                ))}
                required
                label="Área de Atuação"
                formValue={'flTipoMulta'}
              />
              <SelectForm
                control={control}
                errors={errors}
                defaultValue={''}
                placeholder="Selecione"
                listOptions={faixaFuncionariosList?.map((qtdFuncionario) => (
                  { label: qtdFuncionario?.descricao.toString(), value: qtdFuncionario?.faixaId }
                ))}
                label="Faixa de Funcionários"
                formValue={'faixaId'}
              />
              <SelectForm
                control={control}
                errors={errors}
                defaultValue={''}
                placeholder="Selecione"
                listOptions={infracoesList?.map((infracao) => (
                  { label: infracao?.nuNivelInfracao, value: infracao?.idInfracao }
                ))}
                label="Infração"
                formValue={'infracaoId'}
              />
              <InputMask
                control={control}
                errors={errors}
                defaultValue={''}
                label="Quantidade UFIR"
                formValue={'qtUfir'}
                maxLength={20}
                type="number"
                required
              />
              <Button
                variantButton="default"
                text="SALVAR"
                type="submit"
                width={'max-content'}
                marginTop={SPACING.spacing10}
              />
            </Grid>
          </Form>
        </SimpleCollapse>
      </Card>
    </>
  )
}

export default FineGraduation
